import type { CityConfig } from "@/pages/city-template/types";

export const waukeshaConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Waukesha",
  state: "Wisconsin",
  stateAbbr: "WI",
  slug: "waukesha",
  route: "/cities/waukesha",
  title: "Emergency & Expedited Passport Services Waukesha WI | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Waukesha, WI — as fast as 24 hours. No Wisconsin passport agency. Fast Passport Center couriers to Chicago, about 95 miles south. A+ BBB rated. Serving Waukesha County, Brookfield, New Berlin, and the western Milwaukee metro.",
  h1: "Emergency & Expedited Passport Services in Waukesha, WI",
  heroSubtitle: "Waukesha is the heart of Waukesha County, just 95 miles from the Chicago Passport Agency via I-94. Fast Passport Center's registered couriers deliver your documents to Chicago so you get a new or renewed passport as fast as 24 hours — whether you're in downtown Waukesha, Brookfield, New Berlin, or anywhere in the western Milwaukee metro.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Waukesha%20Wisconsin%20downtown%20Fox%20River%20flowing%20through%20historic%20city%20center%20at%20golden%20hour%2C%20warm%20amber%20sunlight%20reflecting%20on%20river%20water%2C%20classic%20Wisconsin%20architecture%20with%20vintage%20downtown%20buildings%2C%20Fox%20Riverwalk%20trail%2C%20professional%20landscape%20photography%2C%20wide%20angle%20view%20showing%20river%20corridor%20and%20urban%20streetscape%2C%20soft%20natural%20light%2C%20no%20text%20or%20people%20visible&width=1800&height=700&seq=waukesha-hero-001&orientation=landscape",
  heroImageAlt: "Waukesha Wisconsin downtown Fox River corridor at golden hour",
  geoPosition: "43.0117;-88.2315",
  geoPlacename: "Waukesha, Wisconsin",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "230 S Dearborn St, Suite 380, Chicago, IL 60604",
    distance: "Approximately 95 miles from Waukesha — about 1.5 hours via I-94 south",
  },
  nearbyCitiesZips: [
    { name: "Downtown Waukesha", zips: "53186, 53188" },
    { name: "West Waukesha", zips: "53189" },
    { name: "Brookfield", zips: "53005, 53045" },
    { name: "New Berlin", zips: "53146, 53151" },
    { name: "Pewaukee", zips: "53072" },
    { name: "Elm Grove", zips: "53122" },
    { name: "Menomonee Falls", zips: "53051" },
    { name: "Muskego", zips: "53150" },
    { name: "Sussex", zips: "53089" },
    { name: "Delafield", zips: "53018" },
    { name: "Oconomowoc", zips: "53066" },
    { name: "Hartland", zips: "53029" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Courier delivery to Chicago Passport Agency — just 95 miles south on I-94" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-map-pin-2-line", title: "Waukesha County Hub", desc: "Serving Brookfield, New Berlin, Pewaukee, and the western Milwaukee metro" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your Waukesha case from start to finish" },
  ],
  internalLinks: [
    { name: "Emergency passport in Milwaukee WI", route: "/cities/milwaukee" },
    { name: "Expedited passport in Madison WI", route: "/cities/madison-wi" },
    { name: "Urgent passport in Racine WI", route: "/cities/racine" },
    { name: "Wisconsin passport hub", route: "/states/wisconsin" },
    { name: "Chicago passport services", route: "/states/illinois" },
    { name: "Emergency passport in West Bend WI", route: "/cities/west-bend" },
    { name: "Expedited passport in Kenosha WI", route: "/cities/kenosha-wi" },
  ],
  fpcOfficeAddress: "833 E Michigan St, Milwaukee, WI 53202",
  mapEmbedUrl: "https://www.google.com/maps?q=833+E+Michigan+St,+Milwaukee,+WI+53202&output=embed",
  fpcOfficeNote: "Fast Passport Center serves Waukesha remotely via secure courier. Our registered couriers hand-deliver your documents to the Chicago Passport Agency. Waukesha's location right on the I-94 corridor — just 95 miles from Chicago — makes it one of the fastest Wisconsin cities for courier turnaround.",
  fpcOfficeDistance: "Serving all of Waukesha County",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Waukesha, Brookfield, New Berlin, and the western Milwaukee metro. From downtown Waukesha along the Fox River to the growing business corridors in Brookfield — we help Waukesha County travelers get their passports fast.",
  nearbyCitiesIntro: "We serve Waukesha and nearby communities including Brookfield, New Berlin, Pewaukee, Elm Grove, Menomonee Falls, Muskego, and the entire Waukesha County region.",
  howItWorksIntro: "Most Waukesha travelers complete everything online and ship documents overnight. At just 95 miles from Chicago on the I-94 corridor, Waukesha residents enjoy some of the fastest courier turnaround times in Wisconsin.",
  mistakesIntro: "Waukesha travelers, including business professionals in Brookfield and families throughout Waukesha County, have unique passport needs. Here are the most common mistakes we see.",
  testimonialsIntro: "Waukesha and Waukesha County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Waukesha? Here are the most common ones we hear from Waukesha County travelers.",
  statePageRoute: "/states/wisconsin",
  statePageName: "Wisconsin",
  dmvWebsite: "https://www.dot.wisconsin.gov/drivers",
  dmvLabel: "Wisconsin DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Direct I-94 corridor to Chicago — fastest WI turnaround",
        "No drive or appointment needed",
        "Ideal for Brookfield business travelers",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service USPS",
        "Good for planned travel from MKE",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "No document review — errors cause rejection",
        "In-person clerk appointment required",
        "Workload shifts with government shutdowns",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "Cannot upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Only 95 miles to Chicago (1.5-hour drive)",
        "Still requires pre-scheduled NPIC appointment",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "Waukesha is only 95 miles from the Chicago Passport Agency on the I-94 corridor. Our couriers achieve same-day delivery for 24-hour turnaround.",
      bullets: ["Direct I-94 corridor to Chicago", "24–48 hour turnaround available", "No appointment or drive needed"],
      badge: "Fastest in WI",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Waukesha adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No acceptance clerk visit for most renewals", "Mail-in or courier options", "Serving Waukesha and Brookfield"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support in Waukesha and Waukesha County.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Fast replacement for Waukesha, Brookfield, and Waukesha County residents with DS-64 form guidance.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement via Chicago Agency"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support in Waukesha County.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport name change service after marriage, divorce, or legal changes for Waukesha and Brookfield residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Waukesha WI", description: "Urgent passport processing for Waukesha, WI travelers via Chicago Passport Agency courier — just 95 miles from Waukesha on the I-94 corridor." },
    { name: "Passport Renewal Waukesha WI", description: "Fast passport renewal for Waukesha and Waukesha County, Wisconsin residents." },
    { name: "New Passport Waukesha WI", description: "First-time passport application assistance for Waukesha and western Milwaukee metro residents." },
    { name: "Lost Passport Waukesha WI", description: "Passport replacement for lost or stolen passports for Waukesha and Brookfield residents." },
    { name: "Child Passport Waukesha WI", description: "Minor passport services for Waukesha County families." },
    { name: "Name Change Passport Waukesha WI", description: "Passport name change service for Waukesha, WI residents." },
  ],
  testimonials: [
    {
      name: "Mark D.",
      location: "Brookfield, WI",
      rating: 5,
      text: "Business trip to London came up on 4 days notice. My passport had expired during COVID and I hadn't renewed. Fast Passport Center got me a new one in 48 hours. Being right on I-94 to Chicago makes a huge difference.",
      service: "Emergency Passport",
    },
    {
      name: "Jennifer S.",
      location: "New Berlin, WI",
      rating: 5,
      text: "Family cruise out of Fort Lauderdale — three of us needed renewals. Fast Passport Center processed all three in 6 business days. Saved us from what would have been a disaster.",
      service: "Family Passports",
    },
    {
      name: "Robert M.",
      location: "Downtown Waukesha",
      rating: 5,
      text: "Carroll University professor heading to a conference in Barcelona. Standard USPS was quoting 10 weeks — I had 3. Fast Passport Center had my renewal back in 5 days. Incredible service.",
      service: "Expedited Renewal",
    },
    {
      name: "Amanda W.",
      location: "Pewaukee, WI",
      rating: 5,
      text: "Lost my passport somewhere between O'Hare and home. Had another international trip in 10 days. Fast Passport Center handled the DS-64 and replacement in 3 business days. Could not be more grateful.",
      service: "Lost Passport",
    },
  ],
  facilities: [
    {
      id: "wauk-1",
      name: "Waukesha Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "235 W Broadway",
      city: "Waukesha",
      state: "WI",
      zip: "53186",
      phone: "(262) 547-8327",
      hours: "Mon–Fri: 8:30 AM – 5:00 PM",
      distance: "Downtown Waukesha",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Waukesha. Appointment required. Call ahead to confirm passport service hours.",
      icon: "ri-mail-line",
    },
    {
      id: "wauk-2",
      name: "Waukesha County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "515 W Moreland Blvd, Room 120",
      city: "Waukesha",
      state: "WI",
      zip: "53188",
      phone: "(262) 548-7012",
      hours: "Mon–Fri: 8:00 AM – 4:00 PM",
      distance: "Waukesha County Courthouse",
      website: "https://www.waukeshacounty.gov/countyclerk",
      websiteLabel: "Waukesha County Clerk",
      notes: "Accepts passport applications for Waukesha County residents. Located in the county courthouse.",
      icon: "ri-government-line",
    },
    {
      id: "wauk-3",
      name: "Brookfield Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "17100 W North Ave",
      city: "Brookfield",
      state: "WI",
      zip: "53005",
      phone: "(262) 782-4995",
      hours: "Mon–Fri: 8:30 AM – 5:30 PM",
      distance: "5 miles east of Waukesha",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Brookfield and eastern Waukesha County residents. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "wauk-4",
      name: "New Berlin Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "15155 W National Ave",
      city: "New Berlin",
      state: "WI",
      zip: "53151",
      phone: "(262) 786-6360",
      hours: "Mon–Fri: 8:30 AM – 5:00 PM",
      distance: "6 miles southeast of Waukesha",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for New Berlin and southeastern Waukesha County residents.",
      icon: "ri-mail-line",
    },
    {
      id: "wauk-5",
      name: "Menomonee Falls Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "N88W16726 Appleton Ave",
      city: "Menomonee Falls",
      state: "WI",
      zip: "53051",
      phone: "(262) 251-1730",
      hours: "Mon–Fri: 8:30 AM – 5:00 PM",
      distance: "12 miles northeast of Waukesha",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Menomonee Falls and northern Waukesha County residents.",
      icon: "ri-mail-line",
    },
    {
      id: "wauk-6",
      name: "Oconomowoc Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "38 S Main St",
      city: "Oconomowoc",
      state: "WI",
      zip: "53066",
      phone: "(262) 567-2536",
      hours: "Mon–Fri: 8:30 AM – 5:00 PM",
      distance: "18 miles west of Waukesha",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Oconomowoc, Delafield, and western Waukesha County residents.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Distance to Chicago Agency", agency: "95 miles — 1.5-hr drive on I-94, but need appointment", fpc: "We drive for you — no appointment needed" },
    { feature: "Appointment Needed", agency: "Yes — call NPIC, very limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-map-pin-line",
      title: "Assuming the I-94 Corridor Makes It Easy",
      description: "Waukesha sits on I-94, just 95 miles from Chicago. But the Chicago Passport Agency still requires pre-scheduled appointments — being close doesn't mean you can walk in. We handle the appointment and delivery for you.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Photo Background",
      description: "Passport photos must have a pure white background — no off-white, cream, or textured walls. CVS and Walgreens in Waukesha and Brookfield offer passport photo services, but always verify the background is truly white.",
    },
    {
      icon: "ri-file-list-line",
      title: "Using an Expired Form",
      description: "The U.S. Department of State updates forms periodically. Make sure you're using the current DS-11 or DS-82 from the official travel.state.gov website — not an outdated version saved on your computer.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "Booking Travel Before Passport Arrives",
      description: "Waukesha County business travelers sometimes book international flights before confirming passport status. Always have a valid passport in hand before booking non-refundable international travel.",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport Without Both Parents",
      description: "Minors under 16 must have both parents present at the acceptance facility, or one with a notarized DS-3053. This catches Waukesha families off guard when one parent is traveling for work.",
    },
    {
      icon: "ri-time-line",
      title: "Not Checking Passport Validity for Business Travel",
      description: "Many countries require 6 months of passport validity beyond your travel dates. Waukesha and Brookfield business travelers often overlook this and get denied at check-in. Check expiration dates before booking.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send a prepaid overnight label. Drop at any UPS location in Waukesha, Brookfield, or New Berlin." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team reviews every document, then couriers to Chicago — just 95 miles via I-94." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at the Chicago Passport Agency via our pre-secured courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Waukesha County address — fully trackable." },
  ],
  faqs: [
    {
      q: "How close is Waukesha to the Chicago Passport Agency?",
      a: "Waukesha is approximately 95 miles from the Chicago Passport Agency — about 1.5 hours via I-94 south. Waukesha is one of the best-positioned Wisconsin cities for fast passport processing thanks to its direct I-94 corridor access.",
    },
    {
      q: "Can I just drive to Chicago myself from Waukesha?",
      a: "You can, but the Chicago Passport Agency requires pre-scheduled appointments — walk-ins are not accepted. The drive is about 1.5 hours each way, and without an appointment you'll be turned away. Fast Passport Center pre-secures courier slots so your documents are accepted on your behalf.",
    },
    {
      q: "How fast can I get an emergency passport in Waukesha?",
      a: "Emergency processing is typically 24–48 hours. Waukesha's location on the I-94 corridor — just 95 miles from Chicago — enables some of the fastest courier turnaround times in Wisconsin.",
    },
    {
      q: "Do you serve Brookfield and New Berlin residents?",
      a: "Yes. We serve all of Waukesha County including Brookfield, New Berlin, Pewaukee, Elm Grove, Menomonee Falls, Muskego, and beyond. Documents ship overnight from any UPS location in Waukesha County.",
    },
    {
      q: "Where do Waukesha travelers fly internationally?",
      a: "Waukesha travelers typically fly internationally through Milwaukee Mitchell International Airport (MKE, 25 miles east) or Chicago O'Hare (ORD, 100 miles south). Both offer extensive international connections. A valid U.S. passport is required for all international flights.",
    },
    {
      q: "How do I renew my passport in Waukesha?",
      a: "Adult renewals (passport issued at 16 or older, within the past 15 years, in your current name) can be done entirely by mail with Form DS-82. Fast Passport Center achieves 2–9 business day turnaround via Chicago courier.",
    },
    {
      q: "What documents do I need for a first-time passport in Waukesha?",
      a: "You'll need Form DS-11, a certified Wisconsin birth certificate, a valid photo ID, two passport photos, and fee payment. First-time applicants must appear in person at an acceptance facility such as the Waukesha Main Post Office or Waukesha County Clerk.",
    },
    {
      q: "Does the Waukesha post office require an appointment for passports?",
      a: "Yes. The Waukesha Main Post Office on W Broadway requires appointments for passport services. Call ahead or use the USPS online scheduler. Walk-in service is not guaranteed for passport applications.",
    },
    {
      q: "Can Carroll University students get fast passports?",
      a: "Yes. Carroll University students studying abroad or traveling internationally are eligible for our expedited services. We recommend starting the process at least 8 weeks before any study abroad departure for standard expedited (2–9 business day) service.",
    },
    {
      q: "What if I need a passport for last-minute international business travel from Waukesha?",
      a: "We specialize in urgent business travel passports. Many Brookfield and Waukesha professionals use our emergency service when international trips come up on short notice. 24–48 hour processing is available with proof of travel.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Waukesha", address: "230 Madison St, Waukesha, WI 53188", hours: "Mon–Sun: 8AM–9PM" },
    { name: "Walgreens — Waukesha", address: "1700 E Main St, Waukesha, WI 53186", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Brookfield", address: "16845 W Greenfield Ave, Brookfield, WI 53005", hours: "Mon–Sun: 8AM–9PM" },
    { name: "FedEx Office — Brookfield", address: "15825 W Bluemound Rd, Brookfield, WI 53005", hours: "Mon–Fri: 7AM–8PM, Sat: 9AM–5PM" },
    { name: "Walgreens — New Berlin", address: "15100 W Greenfield Ave, New Berlin, WI 53151", hours: "Mon–Sun: 7AM–10PM" },
    { name: "UPS Store — Waukesha", address: "2312 N Grandview Blvd, Waukesha, WI 53188", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–4PM" },
  ],
  dmvLocations: [
    { name: "WI DMV — Waukesha", address: "1661 Pearl St, Waukesha, WI 53186", hours: "Mon–Fri: 8:30AM–4:30PM", note: "REAL ID and driver's license renewals for Waukesha County residents.", featured: true },
    { name: "WI DMV — Milwaukee Southwest", address: "5500 W Grange Ave, Greendale, WI 53129", hours: "Mon–Fri: 8:30AM–4:30PM", note: "Closest full-service DMV with extended hours for Milwaukee metro residents.", featured: false },
    { name: "WI DMV — West Bend", address: "903 E Paradise Dr, West Bend, WI 53095", hours: "Mon–Fri: 8:30AM–4:30PM", note: "Serves Washington County and northern Waukesha County residents.", featured: false },
  ],
  airports: [
    {
      code: "MKE",
      name: "Milwaukee Mitchell International Airport",
      distance: "25 miles east of Waukesha",
      address: "5300 S Howell Ave, Milwaukee, WI 53207",
      note: "Waukesha's primary international airport. About 30 minutes east via I-94. Direct international flights to destinations in Mexico, Caribbean, and Canada. Passport required for all international flights.",
      flightUrl: "https://www.mitchellairport.com/",
      flightLabel: "MKE Airport",
      featured: true,
    },
    {
      code: "ORD",
      name: "Chicago O'Hare International Airport",
      distance: "100 miles south of Waukesha",
      address: "10000 W O'Hare Ave, Chicago, IL 60666",
      note: "Major international hub with extensive global connections. About 1.5 hours via I-94 south. Co-located near the Chicago Passport Agency for emergency processing convenience.",
      flightUrl: "https://www.flychicago.com/ohare/",
      flightLabel: "O'Hare Airport",
      featured: false,
    },
    {
      code: "MDW",
      name: "Chicago Midway International Airport",
      distance: "105 miles south of Waukesha",
      address: "5700 S Cicero Ave, Chicago, IL 60638",
      note: "Alternative Chicago airport with some international service. About 1 hour 45 minutes via I-94 south.",
      flightUrl: "https://www.flychicago.com/midway/",
      flightLabel: "Midway Airport",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    { icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from the Wisconsin Department of Health Services Vital Records office. Online, mail, or in-person options for Waukesha County residents.", url: "https://www.dhs.wisconsin.gov/vitalrecords/birth.htm" },
    { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through Wisconsin Vital Records. Required for estate and legal matters in Waukesha County.", url: "https://www.dhs.wisconsin.gov/vitalrecords/death.htm" },
    { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the Waukesha County Register of Deeds at 515 W Moreland Blvd. Certified copies for name changes and passport applications.", url: "https://www.waukeshacounty.gov/registerofdeeds" },
    { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through the Waukesha County Clerk of Circuit Court. Required for name change passport corrections.", url: "https://www.wicourts.gov/courts/circuit/waukesha.htm" },
  ],
};